import { RatesStore } from '../stores/rates';
import { cleanCatalogsStore, getCatalogsRateListTypeSelect } from './catalogs';
import { cleanPurchaseRatesStore } from './purchases';
import { setUiTotal } from './ui';
import { apiManager } from '../REST';
import {
    ICompanyRateItem,
    IFilterParams,
    IIncludeAssignedCompanyItemAPI,
    IItemAPI,
    IParamsList,
    IRateImportItem,
    IRateImportItemAPI,
    IRateImportItemBodyAPI,
    IRateImportItemError,
    IRateImportItemRAW,
    IRateListAPIV2,
    IRateListItem,
    IRateListItemAPI,
    IRateListItemAPIV2,
    IRatesListAPI,
} from '../interfaces';
import { mapApi2UiField } from '../instruments';

// ------------ Main ------------

export const setRatesIsV2 = (isV2?: boolean): void => {
    const StoreInstance = RatesStore.getInstance();
    StoreInstance.setIsV2(isV2);
};

export function getImportRatesFilterParams(): IFilterParams {
    const StoreInstance = RatesStore.getInstance();
    return StoreInstance.filterImportParams;
}

export function cleanRatesStore(): void {
    const StoreInstance = RatesStore.getInstance();
    StoreInstance.cleanStore();
}

export function cleanRatesRelationSelectAndStore(): void {
    cleanRatesStore();
    cleanPurchaseRatesStore();
    cleanCatalogsStore();
}

export function setRatesSelect(data: IItemAPI[]): void {
    const StoreInstance = RatesStore.getInstance();
    StoreInstance.setRateSelect(
        data.map((i) => {
            return { id: i.id, name: i.attributes.name };
        }),
    );
}

export async function getRatesSelect(): Promise<void> {
    const { resp } = await apiManager.getApi('GET_SALES_RATES_SELECT');
    const { data } = resp;
    data && setRatesSelect(data as IItemAPI[]);
}

// ------------ RateList ------------

function setRatesStore(list?: Array<IRateListItem>): void {
    const StoreInstance = RatesStore.getInstance();
    StoreInstance.setList(list || []);
}

function mapRatesList(apiList: IRatesListAPI): Array<IRateListItem> {
    if (apiList.data) {
        const companies: IItemAPI[] = apiList?.included?.filter((i) => i.type === 'company') || [];
        const assignedCompanies = apiList?.included?.filter((i) => i.type === 'assigned_company') || [];
        return apiList.data.map((item: IRateListItemAPI) => {
            const companyId = item.relationships?.company?.data?.id;
            const company = companies.find((i) => i.id === companyId)?.attributes.name;
            const assignedCompaniesList: ICompanyRateItem[] =
                item.relationships?.assigned_companies?.data?.map((ac: IIncludeAssignedCompanyItemAPI) => {
                    const assignedCompanyId =
                        (assignedCompanies.find((f) => f.id === ac.id) as IIncludeAssignedCompanyItemAPI)?.relationships
                            ?.company?.data?.id || '';
                    const assignedCompanyName = companies.find((f) => f.id === assignedCompanyId)?.attributes?.name;

                    return {
                        id: assignedCompanyId,
                        name: assignedCompanyName,
                    };
                }) || [];
            return {
                id: item.id,
                name: item.attributes?.name,
                companyId,
                company,
                assignedCompaniesList,
            };
        });
    } else {
        return [];
    }
}
function mapRatesListV2(lists?: Array<IRateListItemAPIV2>): Array<IRateListItem> {
    return (
        lists?.map((i: IRateListItemAPIV2) => ({
            id: i.id,
            name: i.name,
            companyId: i.company?.id,
            company: i.company?.name,
            assignedCompaniesList: i.applied_companies,
        })) || []
    );
}

export async function getRatesList(params: IParamsList): Promise<void> {
    const { resp } = await apiManager.getApi('GET_SALES_RATES_LIST', { searchParamsList: params });
    resp && setRatesStore(mapRatesList(resp as IRatesListAPI));
    const { meta } = resp;
    meta && setUiTotal(meta.records_count);
}
export async function getRatesListV2(params: IParamsList): Promise<void> {
    const { resp } = await apiManager.getApi('GET_SALES_RATES_LIST_V2', { searchParamsList: params });
    const { meta, ...other } = resp;
    const { sales_rate_lists } = other as IRateListAPIV2;
    resp && setRatesStore(mapRatesListV2(sales_rate_lists));
    meta && setUiTotal(meta.records_count);
}

export function setRatesForcedUpdateTime(): void {
    const StoreInstance = RatesStore.getInstance();
    StoreInstance.setForcedUpdateTime();
}

// ------------ ImportList ------------

export function setRatesForcedImportUpdateTime(): void {
    const StoreInstance = RatesStore.getInstance();
    StoreInstance.setForcedUpdateImportTime();
}
const mapRateImportItem = (i: IRateImportItemAPI): IRateImportItem => ({ id: i.id, ...mapApi2UiField(i) });

export function setImportList(list: IRateImportItemRAW[] = []): void {
    const StoreInstance = RatesStore.getInstance();
    StoreInstance.setImportList(list);
}
export function setImportErrorList(list: IRateImportItemError[] = []): void {
    const StoreInstance = RatesStore.getInstance();
    StoreInstance.setImportErrorList(list);
}

export function updateRateErrorListByStatus(items: IRateImportItemAPI[]) {
    const StoreInstance = RatesStore.getInstance();
    const errorList = StoreInstance.importErrorList;
    const updatedErrorList = errorList.filter((errorItem) => {
        const foundAPIItem = items.find((apiItem) => apiItem.id === errorItem.id);
        return !(foundAPIItem && foundAPIItem.status !== errorItem.status);
    });
    setImportErrorList(updatedErrorList);
}

export async function getImportList(params?: IParamsList): Promise<void> {
    const { resp } = await apiManager.getApi('GET_SALES_RATE_IMPORT_LIST', { searchParamsList: params });
    const meta = resp.meta;
    if (resp.data && Array.isArray(resp.data)) {
        const mapList = resp.data.map((i) => {
            return mapRateImportItem(i as unknown as IRateImportItemAPI);
        });
        updateRateErrorListByStatus(resp.data as unknown as IRateImportItemAPI[]);
        setImportList(mapList);
    } else {
        setImportList([]);
    }

    meta && setUiTotal(meta.records_count);
}

export function setRateListItem(item: IRateImportItemAPI) {
    const StoreInstance = RatesStore.getInstance();
    const list = StoreInstance.importList.map((i) => {
        if (i.id === item.id) {
            return mapRateImportItem(item);
        } else {
            return i;
        }
    });
    setImportList(list);
}
export function setRateErrorListItem(item: IRateImportItemAPI) {
    const StoreInstance = RatesStore.getInstance();
    let isFoundItem = false;
    const list = StoreInstance.importErrorList.map((i) => {
        if (i.id === item.id) {
            isFoundItem = true;
            return mapRateImportItem(item);
        } else {
            return i;
        }
    });
    if (!isFoundItem) {
        list.push(mapRateImportItem(item));
    }
    setImportErrorList(list);
}

export async function getRateListItem(id: number): Promise<void> {
    const apiResponse = await apiManager.getApi('GET_SALES_RATE_IMPORT', { id: id.toString() });
    const { resp } = apiResponse as IRateImportItemBodyAPI;
    const item = resp?.data_import;
    if (item) {
        setRateListItem(item);
        setRateErrorListItem(item);
    }
}
export function updateRateListItem(id: number): void {
    getRateListItem(id);
}

export function getRateListRelationSelect(): void {
    getCatalogsRateListTypeSelect();
}
